import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Kanban,
  List,
  Package,
  Workflow,
  GitBranch,
  MessageSquare,
  Settings,
  TrendingUp,
  LogOut,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuth } from '@/lib/auth';

interface NavItem {
  href: string;
  label: string;
  icon: React.ReactNode;
}

const navItems: NavItem[] = [
  { href: '/', label: 'Dashboard', icon: <LayoutDashboard className="h-4 w-4" /> },
  { href: '/pipeline', label: 'Pipeline', icon: <Kanban className="h-4 w-4" /> },
  { href: '/deals', label: 'Deals', icon: <List className="h-4 w-4" /> },
  { href: '/bundles', label: 'Bundles', icon: <Package className="h-4 w-4" /> },
  { href: '/workflows', label: 'Workflows', icon: <Workflow className="h-4 w-4" /> },
  { href: '/flowcharts', label: 'Flowcharts', icon: <GitBranch className="h-4 w-4" /> },
  { href: '/negotiations', label: 'Negotiations', icon: <MessageSquare className="h-4 w-4" /> },
  { href: '/settings', label: 'Settings', icon: <Settings className="h-4 w-4" /> },
];

export interface HeaderProps {
  className?: string;
}

/**
 * Professional Header Component
 * Bloomberg Terminal-inspired design for institutional finance applications
 *
 * Features:
 * - Clean, minimal design with strong visual hierarchy
 * - Premium gold accent for branding
 * - Professional navigation with icons
 * - Fixed positioning for persistent access
 * - Responsive layout
 * - Dark mode ready
 */
function LogoutButton(): React.ReactElement {
  const { logout } = useAuth();

  return (
    <button
      onClick={logout}
      className={cn(
        'flex items-center gap-2',
        'px-3 py-2 rounded-md',
        'text-sm font-medium',
        'text-text-secondary hover:bg-surface-hover hover:text-text-primary',
        'transition-all duration-base'
      )}
      title="Sign out"
    >
      <LogOut className="h-4 w-4" />
      <span className="hidden lg:inline">Sign Out</span>
    </button>
  );
}

export function Header({ className }: HeaderProps): React.ReactElement {
  const location = useLocation();

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0',
        'bg-surface border-b border-border',
        'shadow-sm',
        'z-fixed',
        className
      )}
    >
      <div className="flex items-center justify-between h-16 px-6">
        {/* Logo and Brand */}
        <div className="flex items-center gap-8">
          <Link
            to="/"
            className="flex items-center gap-3 group"
          >
            <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-md">
              <TrendingUp className="h-5 w-5 text-white" />
            </div>
            <div className="flex flex-col">
              <h1 className="text-base font-semibold text-text-primary leading-none">
                Secondary Market
              </h1>
              <span className="text-xs text-text-accent font-medium">
                LP Investment Platform
              </span>
            </div>
          </Link>

          {/* Main Navigation */}
          <nav className="flex items-center gap-1" aria-label="Main navigation">
            {navItems.map((item) => {
              const isActive = location.pathname === item.href;

              return (
                <Link
                  key={item.href}
                  to={item.href}
                  className={cn(
                    'flex items-center gap-2',
                    'px-3 py-2 rounded-md',
                    'text-sm font-medium',
                    'transition-all duration-base',
                    isActive
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-text-secondary hover:bg-surface-hover hover:text-text-primary'
                  )}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {item.icon}
                  <span className="hidden lg:inline">{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </div>

        {/* User Actions */}
        <div className="flex items-center gap-4">
          <LogoutButton />
        </div>
      </div>
    </header>
  );
}

/**
 * Header Spacer Component
 * Use this component to add appropriate spacing below the fixed header
 */
export function HeaderSpacer(): React.ReactElement {
  return <div className="h-16" aria-hidden="true" />;
}
